import React, {useEffect, useMemo, useState} from "react";
import {Button} from "../../../../stories/Button/Button";
import EditProfile from "../../../../images/EditProfile.svg";
import Delete from "../../../../images/Delete.svg";
import useAssetTableJson from "../../../../json/useAssetTableJson";
import {TableHeaderProps} from "../../../../utils/types";
import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import {StyledDatagrid} from "../../../../styles/DatagridStyles";
import {ColumnVisibilityFor} from "../../../../utils/Enum.Datagrid";
import {TABLE_ROW_HEIGHT} from "../../../../styles/Variables";
import {Title} from "../../../../stories/Title/Title";
import Close from "../../../../images/Close.svg";
import {Label} from "../../../../stories/Label/Label";
import {useLazyQuery} from "@apollo/client";
import {GetBlowCountDetails} from "../masters/items/queries/queries";
import {
  AtsCalibrationDetailsEdge,
  AtsCalibrationDetailsOrderField,
  GetBlowCountDetailsResult,
  GetCalibrationDetailsVars,
} from "../masters/items/queries/types";
import useUrlParams from "../../../../customHooks/useUrlParams";
import {EMPTY_STRING, ROWS_PER_PAGE} from "../../../../utils/constants";
import {OrderDirection} from "../../../../utils/commonQueryTypes";
import {toStandardDate} from "../../../../utils/UtilFunctions";
import useLoggedInUserDetails from "../../../../customHooks/useLoggedInUserDetails";

export interface calDetailsType {
  id: number;
  desc: string;
  blow_count: number;
  timesCalibrated: number;
  calibrationLimit: number;
}
interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  calDetails: calDetailsType | null;
}

const View = ({setModalFlag, calDetails}: Props) => {
  const {Calibration} = useAssetTableJson();

  const {customer_id} = useUrlParams();

  const {user_details} = useLoggedInUserDetails();

  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [items, setItems] = useState<AtsCalibrationDetailsEdge[]>([]);

  const [getCalibration, {data, loading}] = useLazyQuery<
    GetBlowCountDetailsResult,
    GetCalibrationDetailsVars
  >(GetBlowCountDetails);
  const dynamicHeaders: TableHeaderProps[] = Calibration.List.filter(
    (header) =>
      header.field !== "enter_blow_count" &&
      header.field !== "total_times" &&
      header.field !== "serial_no" &&
      header.field !== "blow_count" &&
      header.field !== "actions"
  ).map((header) => ({
    headerName: header.headerName,
    field: header.field,
    headerAlign: header.headerAlign as GridAlignment,
    align: header.align as GridAlignment,
    flex: header.flex,
    hideable: header.hideable,
    cellClassName: header.cellClassName,
    renderCell: (params) => {
      if (header.field === "actions") {
        return (
          <>
            <img src={EditProfile} alt="" />
            <img src={Delete} alt="" />
          </>
        );
      }

      return params.value;
    },
  }));
  const columns: GridColDef[] = [...dynamicHeaders];

  const rows: GridValidRowModel[] = useMemo(() => {
    return items.map(({node}, index) => {
      return {
        id: index + 1,
        blow_date: toStandardDate(node.blowDate),
        blow_count_to_view: node.blowCount,
        total_times: 0,
      };
    });
  }, [items]);

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});

  useEffect(() => {
    const savedModel = localStorage.getItem(
      ColumnVisibilityFor.CALIBRATION_DETAILS
    );
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.CALIBRATION_DETAILS,
      JSON.stringify(newModel)
    );
  };

  useEffect(() => {
    if (customer_id && calDetails)
      getCalibration({
        variables: {
          after: null,
          customer_id,
          first: ROWS_PER_PAGE,
          empID: user_details.id,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsCalibrationDetailsOrderField.CREATED_AT,
          },
          itemDetailsID: calDetails.id,
          itemID: 0,
          name: EMPTY_STRING,
        },
      });
  }, [customer_id, calDetails, getCalibration]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetBlowCountDetails.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
      } else {
        setItems(newData);
      }
      setEndCursor(data.GetBlowCountDetails.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  return (
    <div className="calibration-view">
      <div className="calibration-view__title">
        <Title>Calibration Details</Title>
        <img src={Close} alt="" onClick={() => setModalFlag(false)} />
      </div>

      {calDetails ? (
        <div className="calibration-view__block">
          <div className="calibration-view__block--grid">
            <Label>Serial No.</Label>
            <span>:</span>
            <b className="font-blue">{calDetails.desc}</b>
          </div>
          <div className="calibration-view__block--grid">
            <Label>Blow Count To Reach</Label>
            <span>:</span>
            <div>
              <b className="font-green">{calDetails.blow_count}</b>/
              <b className="font-red">{calDetails.calibrationLimit}</b>{" "}
            </div>
          </div>
          <div className="calibration-view__block--grid">
            <Label>Total Times Calibrated</Label>
            <span>:</span>
            <b>{calDetails.timesCalibrated}</b>
          </div>
        </div>
      ) : null}

      <div className="calibration-view__tableblock">
        <StyledDatagrid
          columns={columns}
          rows={rows}
          rowHeight={TABLE_ROW_HEIGHT}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          initialState={{
            columns: {
              columnVisibilityModel: columnVisibilityModel,
            },
          }}
          disableRowSelectionOnClick
          hideFooter
        />
      </div>
      <Button mode="cancel" onClick={() => setModalFlag(false)} />
    </div>
  );
};

export default View;
